/**
 * 左侧菜单的二级业务分组配置
 *
 * 设计目的：
 * 同一个菜单组（如"房产"）下，可能存在很多平铺的子菜单。
 * 把同业务域的子菜单折叠到一组下，减少视觉噪音，提升找菜单效率。
 *
 * 工作机制：
 * 1. 在 layout.vue 渲染 subMenus 前，调用 groupSubMenus 把当前 subMenus 按 menuGroups 规则分组。
 * 2. 同一菜单只能归入一个分组（按 menuGroups 顺序匹配，命中即占用）。
 * 3. crossGroup=true 时，如果当前菜单组的 subMenus 中没匹配到该分组的项，
 *    会从当前 catalog 下所有菜单组（allMenuPool）中捞取匹配项。
 *    这样可以让某些菜单在它的"主菜单组"和"被引用的菜单组"下都能看到。
 * 4. 没匹配到任何分组的菜单会单独放到一个 "__others__" 组，保持原样展示。
 *
 * 注意事项：
 * - 这里改的是 UI 渲染层，菜单数据、后端接口、权限（p_privilege.mId）都不动。
 * - 新增业务分组只需在这里加一条规则，不需要改 layout.vue。
 * - 修改/重命名了菜单名称时，记得同步这里 match 函数里的字符串。
 */

// 业务分组规则
// key: 分组唯一标识，用于折叠状态持久化
// name: 分组显示名（目前直接写中文；后续如需多语言可以改成 i18n key）
// icon: 分组左侧小图标（FontAwesome class）
// match: 子菜单命中函数，根据 name 判断是否归入本组
// crossGroup: 是否允许跨菜单组汇总
export const menuGroups = [
  {
    key: 'owner',
    name: '业主',
    icon: 'fa fa-user',
    match: (name) => ['业主信息', '业主认证', '业主账户', '关注用户'].includes(name),
    crossGroup: false
  },
  {
    key: 'venue',
    name: '场地',
    icon: 'fa fa-calendar',
    match: (name) => ['场地管理', '场地预约', '场地预约订单', '预约核销'].includes(name),
    crossGroup: false
  },
  {
    key: 'invoice',
    name: '发票',
    icon: 'fa fa-file-text-o',
    match: (name) => ['发票', '发票抬头'].includes(name),
    crossGroup: false
  },
  // 物品放行（小区菜单组下：放行类型 + 物品放行）
  {
    key: 'itemRelease',
    name: '物品放行',
    icon: 'fa fa-truck',
    match: (name) => ['放行类型', '物品放行'].includes(name),
    crossGroup: false
  },
  // 考勤信息（OA 工作流菜单组下：考勤班组/考勤人员/今日考勤/月考勤/员工考勤/考勤记录/请假审批/加班审批/请假记录/加班记录）
  {
    key: 'attendance',
    name: '考勤信息',
    icon: 'fa fa-clock-o',
    match: (name) => ['考勤班组', '考勤人员', '今日考勤', '月考勤', '员工考勤', '考勤记录', '请假审批', '加班审批', '请假记录', '加班记录'].includes(name),
    crossGroup: false
  },
  // 工作单信息（OA 工作流菜单组下：工作单类型/起工作单/办工作单/抄送我的/工作单池）
  {
    key: 'workOrder',
    name: '工作单信息',
    icon: 'fa fa-list-alt',
    match: (name) => ['工作单类型', '起工作单', '办工作单', '抄送我的', '工作单池'].includes(name),
    crossGroup: false
  },
  // 社区活动（信息发布菜单组下：信息大类 + 信息发布）
  {
    key: 'communityActivity',
    name: '社区活动',
    icon: 'fa fa-bullhorn',
    match: (name) => ['信息大类', '活动大类', '信息发布', '活动发布'].includes(name),
    crossGroup: false
  },
  // 业主投诉（信息发布菜单组下：投诉类型/投诉建议/业主反馈）
  {
    key: 'ownerComplaint',
    name: '业主投诉',
    icon: 'fa fa-commenting',
    match: (name) => ['投诉类型', '投诉建议', '业主反馈'].includes(name),
    crossGroup: false
  },
  // 问卷投票（信息发布菜单组下：问卷题目/问卷调研/业主投票）
  {
    key: 'questionnaire',
    name: '问卷投票',
    icon: 'fa fa-pencil-square-o',
    match: (name) => ['问卷题目', '问卷调研', '业主投票'].includes(name),
    crossGroup: false
  }
]

// 未匹配分组的菜单是否独立展示（"其他"分组）
// 设为 false 则未匹配的菜单直接平铺展示，不显示分组标题
export const showOthersGroup = true

const OTHERS_KEY = '__others__'

/**
 * 把当前 subMenus 按 menuGroups 配置分组
 *
 * @param {Array} subMenus 当前菜单组的子菜单
 * @param {Array} allMenuPool 当前 catalog 下所有菜单组的所有子菜单（用于 crossGroup）
 * @param {String} currentMenuName 当前主菜单名（如 "报修"、"采购"），未匹配分组的菜单单独展示时使用该名作为标题
 * @returns {Array} [{ key, groupName, icon, items }, ...]
 */
export function groupSubMenus(subMenus, allMenuPool = [], currentMenuName = '') {
  // 与原 layout 模板中 v-if="subMenu.isShow == 'Y'" 等价：先过滤掉不显示的菜单
  // 避免在 v-for 中再使用 v-if 触发 vue/no-use-v-if-with-v-for
  const visible = (arr) => (arr || []).filter(m => m && m.isShow == 'Y')
  const subMenusVisible = visible(subMenus)
  const allMenuPoolVisible = visible(allMenuPool)
  const used = new Set()
  const groups = []

  for (const cfg of menuGroups) {
    const items = []
    // 第一轮：从当前 subMenus 中匹配
    for (const m of subMenusVisible) {
      if (!m || !m.name) continue
      if (used.has(_idOf(m))) continue
      if (cfg.match(m.name)) {
        items.push(m)
        used.add(_idOf(m))
      }
    }
    // 第二轮：如果允许跨组且当前没匹配到，从 allMenuPool 中捞
    if (cfg.crossGroup && items.length === 0 && Array.isArray(allMenuPoolVisible)) {
      for (const m of allMenuPoolVisible) {
        if (!m || !m.name) continue
        if (used.has(_idOf(m))) continue
        if (cfg.match(m.name)) {
          items.push(m)
          used.add(_idOf(m))
        }
      }
    }
    if (items.length > 0) {
      groups.push({
        key: cfg.key,
        name: cfg.name,
        icon: cfg.icon,
        items
      })
    }
  }

  if (showOthersGroup) {
    const others = subMenusVisible.filter(m => m && m.name && !used.has(_idOf(m)))
    if (others.length > 0) {
      groups.push({
        key: OTHERS_KEY,
        // 用当前主菜单名（如"报修"、"采购"）替代硬编码的"其他"
        // 没传 currentMenuName 时保留原"其他"以兼容老调用
        name: currentMenuName || '其他',
        icon: '',
        items: others
      })
    }
  }

  return groups
}

function _idOf(m) {
  return m.mId || m.name
}